import React, { useState } from "react";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { ExclamationCircleFill } from "react-bootstrap-icons";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import useMicroSiteEncrypt from "../CustomHook/useMicroSiteEncrypt";
import { submitLeadForm } from "../../redux/actions/micrositeAction";
import "./LeadPopup.css";

export default function LeadPopup({ open, onClose, colors }) {
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { microSiteEncrypt } = useMicroSiteEncrypt();
  const home_data = useSelector((state) => state.microsite?.home_data);
  const url_data = useSelector((state) => state.microsite?.url_data);
  const current_flow = useSelector((state) => state.microsite?.home_data?.flow);
  const customize_fields = useSelector(
    (state) => state.microsite?.home_data?.customization
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [mobile, setMobile] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(true);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [mobileError, setMobileError] = useState(false);

  const primary = colors?.primary || "#0082a0";
  const secondary = colors?.secondary || "#e76408";
  const logo = home_data?.company_logo;
  const clientName =
    home_data?.microsite?.client_name ||
    home_data?.new_microsite_extra_clt?.client_name ||
    "SalesPanda";
  const advisorName = home_data?.microsite?.name || "our advisor";

  const captcha_key =
    window.location.origin.includes("spdevmfp") ||
    window.location.origin.includes("localhost")
      ? "6LfunNgbAAAAAEM0X3hcwSBYKJwIqHmmInfz0uFK"
      : window.location.origin.includes("sppreprod") ||
        window.location.origin.includes("sptestmfp")
      ? "6Ldeg6YaAAAAAAZqU7AQ_j-lG4LcnwP4jfNFiCJJ"
      : "6Lf9fqoaAAAAAH8JhnTg9gLI3KsYKmR2tdCRVg8i";

  const nameRequired =
    customize_fields?.top_contact?.name?.validation === "required";
  const phoneRequired =
    customize_fields?.top_contact?.phone?.validation === "required";
  const emailRequired =
    customize_fields?.top_contact?.email?.validation === "required";

  const handleCountryChange = (value) => {
    if (value) {
      try {
        const phoneNumberObj = parsePhoneNumber(value);
        setCountryCode("+" + phoneNumberObj?.countryCallingCode);
      } catch (error) {
        console.error("Invalid phone number", error);
      }
    } else {
      setCountryCode("");
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setMobile("");
    setCountryCode("+91");
    setMessage("");
    setConsent(true);
    setNameError(false);
    setEmailError(false);
    setMobileError(false);
  };

  const onSuccess = () => {
    resetForm();
    onClose?.();
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();

    if (name === "" && nameRequired) {
      setNameError(true);
      return;
    }
    if ((mobile === "" || mobileError) && phoneRequired) {
      setMobileError(true);
      return;
    }
    if ((email === "" && emailRequired) || emailError) {
      setEmailError(true);
      return;
    }

    /* getNewRecaptchaToken() appended a fresh <script> tag and waited on its
       onload. GoogleReCaptchaProvider (App.js) has already loaded that same
       script, so the browser serves it from cache without re-executing it,
       onload never fires and the promise stays pending forever -- the await
       below then resolved to undefined and we fell back to the stale
       recaptchaToken from mount, which Google rejected as invalid-input-response.
       executeRecaptcha() from the provider mints a fresh token off the script
       that is already there. */
    let freshRecaptchaToken = "";
    if (executeRecaptcha) {
      try {
        freshRecaptchaToken = await executeRecaptcha("lead_form");
      } catch (err) {
        console.error("reCAPTCHA execute failed:", err);
      }
    }
    if (!freshRecaptchaToken) {
      return;
    }
    const currentUrl = window?.location?.href || url_data || "";

    const payload = {
      fullname: name,
      phoneNo: mobile ? `${countryCode}-${mobile.replace(countryCode, "")}` : "",
      emailId: email,
      message,
      recaptcha_token: freshRecaptchaToken,
      url: currentUrl,
    };

    const token = await microSiteEncrypt(payload);
    if (token && token !== "") {
      dispatch(
        submitLeadForm(token, payload, "Lead Popup", current_flow, onSuccess)
      );
    }
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-[110]" onClose={() => onClose?.()}>
        <TransitionChild
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <TransitionChild
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-2"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-2"
            >
              <DialogPanel
                className="lead-popup-modal"
                style={{
                  "--lead-primary": primary,
                  "--lead-secondary": secondary,
                  "--lead-secondary-dark": secondary,
                  "--lead-focus": `${secondary}33`,
                }}
              >
                <button
                  type="button"
                  className="lead-popup-close"
                  aria-label="Close"
                  onClick={() => onClose?.()}
                >
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path
                      d="M1 1L14 14M14 1L1 14"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                <div className="lead-popup-header">
                  {logo ? (
                    <img src={logo} alt={clientName} className="lead-popup-logo" />
                  ) : (
                    <div className="lead-popup-logo-fallback" style={{ backgroundColor: primary }} />
                  )}
                </div>

                <div className="lead-popup-body">
                  
                  <h1 className="lead-popup-title">
                    You&apos;ve made the <em>right</em> choice.
                  </h1>
                  <p className="lead-popup-subhead">
                    Share a few details
                  </p>

                  <form className="lead-popup-form" onSubmit={submitForm}>
                    <div className="lead-popup-row">
                      <div className="lead-popup-field">
                        <input
                          type="text"
                          placeholder="Your Name"
                          value={name}
                          onChange={(e) => {
                            const regex = /^[a-zA-Z ]*$/;
                            if (regex.test(e.target.value)) {
                              setName(e.target.value);
                            }
                            if (e.target.value !== "") setNameError(false);
                          }}
                        />
                       
                        {nameError && (
                          <ExclamationCircleFill
                            title="Enter a name"
                            className="lead-popup-error-icon"
                          />
                        )}
                      </div>
                      <div className="lead-popup-field lead-popup-phone">
                        <PhoneInput
                          flags={flags}
                          placeholder="Mobile number"
                          value={mobile}
                          defaultCountry="IN"
                          limitMaxLength
                          countryCallingCodeEditable={false}
                          international={true}
                          onChange={(value) => {
                            if (value) {
                              setMobile(value);
                              handleCountryChange(value);
                              setMobileError(!isValidPhoneNumber(value));
                            } else {
                              setMobile("");
                              setCountryCode("");
                              setMobileError(false);
                            }
                          }}
                        />
                        {mobileError && (
                          <ExclamationCircleFill
                            title="Enter a valid mobile number"
                            className="lead-popup-error-icon"
                          />
                        )}
                      </div>
                    </div>
                    <div className="lead-popup-row">
                      
                      <div className="lead-popup-field">
                        <input
                          type="email"
                          placeholder="Your email"
                          value={email}
                          onChange={(e) => {
                            const regex =
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                            if (regex.test(e.target.value) || e.target.value === "") {
                              setEmailError(false);
                            } else {
                              setEmailError(true);
                            }
                            setEmail(e.target.value);
                          }}
                        />

                        {emailError && (
                          <ExclamationCircleFill
                            title="Enter a valid email"
                            className="lead-popup-error-icon"
                          />
                        )}
                      </div>
                    </div>

                    <div className="lead-popup-field">
                      <textarea
                        placeholder="Message (optional)"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </div>

                    <label className="lead-popup-consent">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                      />
                      <p>
                        <strong>{clientName || "We"}</strong> will send you updates
                        on products and services, investment solutions or related
                        information.
                      </p>
                    </label>


                    <button type="submit" className="lead-popup-submit">
                      Submit
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M3 8H13M13 8L9 4M13 8L9 12"
                          stroke="white"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <div className="lead-popup-trust">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M6 1L10.5 3V6C10.5 8.5 8.5 10.3 6 11C3.5 10.3 1.5 8.5 1.5 6V3L6 1Z"
                          stroke="currentColor"
                          strokeWidth="1"
                        />
                      </svg>
                      Your information is secure and never shared without consent
                    </div>
                  </form>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
